import { useEffect, useRef, useState } from "react";
import { Bold, Italic, Underline, Link2, Link2Off, Eraser } from "lucide-react";

const exec = (cmd, value) => document.execCommand(cmd, false, value);

const Tool = ({ icon: Icon, label, active, onClick, testid }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={!!active}
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    data-testid={testid}
    className={`p-1.5 rounded-md border transition-colors ${
      active
        ? "border-[#00e599] text-[#00e599] bg-[#00e599]/10"
        : "border-[#38475c] text-slate-400 hover:text-slate-100 hover:border-slate-500"
    }`}
  >
    <Icon size={14} />
  </button>
);

const escapeText = (text) =>
  (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Every body is edited as one flow of lines (the "Invitation" style): paragraph/div blocks from
// older saved wording become line breaks, and plain-text newlines become line breaks too.
const toEditorHtml = (value) => {
  const raw = value || "";
  if (!/<[a-zA-Z/]/.test(raw)) return escapeText(raw).replace(/\n/g, "<br>");
  return raw
    .replace(/<br\s*\/?>/gi, "<br>")
    .replace(/<br>\s*(<\/(?:div|p)>)/gi, "$1")
    .replace(/<\/(?:div|p)>\s*/gi, "<br>")
    .replace(/<(?:div|p)\b[^>]*>/gi, "")
    .replace(/(?:<br>\s*)+$/i, "");
};

export const RichText = ({ value, onChange, testid = "rich-body" }) => {
  const ref = useRef(null);
  const [marks, setMarks] = useState({});

  useEffect(() => {
    const html = toEditorHtml(value);
    // Never rewrite the DOM while the organiser is typing in it — that resets the caret.
    if (ref.current && document.activeElement !== ref.current && ref.current.innerHTML !== html) {
      ref.current.innerHTML = html;
    }
  }, [value]);

  const refreshMarks = () => {
    try {
      setMarks({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
    } catch {
      setMarks({});
    }
  };

  const push = () => {
    // Chrome parks the caret on a trailing <br> after Enter; that one is scaffolding, not content.
    const html = (ref.current?.innerHTML || "").replace(/<br\s*\/?>\s*$/i, "");
    onChange(html);
    refreshMarks();
  };

  const run = (cmd) => () => {
    ref.current?.focus();
    exec(cmd);
    push();
  };

  const addLink = () => {
    ref.current?.focus();
    const selected = window.getSelection()?.toString();
    if (!selected) {
      alert("Select the words you want to turn into a link first.");
      return;
    }
    const url = window.prompt("Link address (must start with https://)", "https://");
    if (!url) return;
    if (!/^https:\/\/|^mailto:/i.test(url.trim())) {
      alert("Links must start with https:// (or mailto: for an email address).");
      return;
    }
    exec("createLink", url.trim());
    push();
  };

  return (
    <div data-testid={`${testid}-wrapper`}>
      <div className="flex items-center gap-1.5 mb-2 flex-wrap">
        <Tool icon={Bold} label="Bold" active={marks.bold} onClick={run("bold")} testid={`${testid}-bold`} />
        <Tool icon={Italic} label="Italic" active={marks.italic} onClick={run("italic")} testid={`${testid}-italic`} />
        <Tool icon={Underline} label="Underline" active={marks.underline} onClick={run("underline")} testid={`${testid}-underline`} />
        <span className="w-px h-5 bg-[#28323f] mx-1" />
        <Tool icon={Link2} label="Add link" onClick={addLink} testid={`${testid}-link`} />
        <Tool icon={Link2Off} label="Remove link" onClick={run("unlink")} testid={`${testid}-unlink`} />
        <Tool icon={Eraser} label="Clear formatting" onClick={run("removeFormat")} testid={`${testid}-clear`} />
        <span className="font-mono2 text-[10px] text-slate-600 ml-auto">bold · italic · links</span>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label="Email body"
        onInput={push}
        onBlur={push}
        onKeyDown={(e) => {
          // Enter is one line break, never a new paragraph block, so spacing is identical in
          // every tab, the preview and Outlook. Two presses give a real blank line.
          if (e.key === "Enter") {
            e.preventDefault();
            if (!exec("insertLineBreak")) exec("insertHTML", "<br>");
            push();
          }
        }}
        onKeyUp={refreshMarks}
        onMouseUp={refreshMarks}
        onPaste={(e) => {
          // Paste as plain text so nothing unexpected sneaks in from Word or a webpage.
          e.preventDefault();
          // insertText turns newlines into <div> blocks, so paste line breaks as <br> ourselves.
          const text = e.clipboardData.getData("text/plain");
          exec("insertHTML", escapeText(text).replace(/\r?\n/g, "<br>"));
          push();
        }}
        data-testid={testid}
        className="input min-h-[180px] whitespace-pre-wrap leading-relaxed focus:outline-none focus:border-[#00e599]"
      />
    </div>
  );
};
